import { DOCUMENT } from '@angular/common'
import { inject, Injectable } from '@angular/core'
import { distinctUntilChanged, map, skipWhile } from 'rxjs'
import { UsersService } from './users.service'
import { DARK_THEME_CLASS, themeColor } from './theme-color'

// Chart.js's own defaults, restored when dark mode is turned off again.
const CHART_DEFAULT_TEXT_COLOR = '#666'
const CHART_DEFAULT_BORDER_COLOR = 'rgba(0,0,0,0.1)'

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private document = inject(DOCUMENT)
  private usersService = inject(UsersService)

  // Gated on the role as well as the stored flag, so a user moved out of support stops getting a theme they can no longer switch off.
  isDarkModeActive$ = this.usersService.currentUser$.pipe(
    map((user) => user !== null && user.role === 'SUPPORT' && user.isDarkModeEnabled),
    distinctUntilChanged()
  )

  initialize() {
    this.isDarkModeActive$.subscribe((isDarkModeActive) => this.document.documentElement.classList.toggle(DARK_THEME_CLASS, isDarkModeActive))
    // Chart.js is only loaded by the dashboards, so it is not pulled in for anyone who never turns dark mode on.
    this.isDarkModeActive$.pipe(skipWhile((isDarkModeActive) => !isDarkModeActive)).subscribe((isDarkModeActive) => this.applyChartDefaults(isDarkModeActive))
  }

  private async applyChartDefaults(isDarkModeActive: boolean) {
    const { Chart } = await import('chart.js')
    Chart.defaults.color = isDarkModeActive ? themeColor('font') : CHART_DEFAULT_TEXT_COLOR
    Chart.defaults.borderColor = isDarkModeActive ? themeColor('border') : CHART_DEFAULT_BORDER_COLOR
  }
}
